/**
 * relay-S3-sdlcfilters — Per-project fixed SDLC filter set.
 *
 * Sprint 48 streamlined defaults (seq #22016):
 *   Project Background → decisions/artifacts/pre-flights/builds/tests/
 *   blockers/findings/metrics removed; Background + Activity + Requirements
 *   + Decisions + Backlog remain.
 *
 * `target` controls what clicking the row does:
 *   - `background` — open the Project Background modal for the project.
 *     Row label renamed "Create-A-Project" (track:project-background-page
 *     rename) — `key`/`target` unchanged, this is a display-only rename.
 *   - `activity` — stay in the activity stream; project name goes onto
 *     `activeFilters` (AND), and `tags` (if present) becomes the OR-mode
 *     `sdlcTagSet` so multi-tag filters like Requirements match either tag.
 *   - `decisions` — switch `centerView` to the Decisions panel,
 *     project-scoped (existing `openProjectDecisions` path).
 *   - `backlog` — switch `centerView` to the dedicated Backlog page,
 *     pre-filtered to this project (`openProjectBacklog` path). Was
 *     previously shipped as `target: "activity"` (same as Requirements) —
 *     that only applied a backlog/spec-ready tag filter to the Activity
 *     stream, never actually navigated to the Backlog page the row's own
 *     label promised.
 *
 * (An `artifacts` target existed pre-Sprint-48 for a placeholder Artifacts
 * panel; removed entirely — ui/left-nav-filter-cleanup — along with the
 * dead `ERArtifactsPanel` component and its handler chain.)
 */

export type SDLCFilterTarget = "activity" | "decisions" | "background" | "backlog";

export interface SDLCFilter {
  /** Stable identifier; used in state and tests. */
  key: string;
  /** Sidebar display label. */
  label: string;
  /**
   * Tags this filter matches. OR semantics in the activity stream.
   * Empty array means "no tag scope" (used by Activity and Background rows).
   */
  tags: string[];
  target: SDLCFilterTarget;
}

export const SDLC_FILTERS: readonly SDLCFilter[] = [
  { key: "background", label: "Create-A-Project", tags: [], target: "background" },
  { key: "activity", label: "Activity", tags: [], target: "activity" },
  { key: "requirements", label: "Requirements", tags: ["spec", "idea", "backlog"], target: "activity" },
  { key: "decisions", label: "Decisions", tags: ["decision"], target: "decisions" },
  { key: "backlog", label: "Backlog", tags: ["backlog", "spec-ready"], target: "backlog" },
] as const;

/** Map for O(1) key lookup in handlers / state derivations. */
export const SDLC_FILTERS_BY_KEY: ReadonlyMap<string, SDLCFilter> = new Map(
  SDLC_FILTERS.map((f) => [f.key, f]),
);

/** Convenience set for "is this user-typed string a fixed filter key?". */
export const SDLC_FILTER_KEYS: ReadonlySet<string> = new Set(
  SDLC_FILTERS.map((f) => f.key),
);
